import { ArrowRightIcon } from "@radix-ui/react-icons";
import { Button, ButtonColor } from "components/Button";
import { useContext, useEffect, useState } from "react";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";
import { AccountStatus } from "types";
import { ApiError } from "client/openapi";
import { AccountsService } from "client/openapi";
import { useAuth0 } from "@auth0/auth0-react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import Notifications from "util/notifications";

function SignupOrgAdmin({ setStep }: { setStep: (step: number) => void }) {
  const { user } = useAuth0();
  const { updateOrgRolesAndAccount } = useContext(OrgRolesAndAccountContext);
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminPhoneNumber, setAdminPhoneNumber] = useState("");
  const [error, setError] = useState<ApiError>();
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const isFormValid = () => {
    return (
      adminFirstName.trim() !== "" &&
      adminLastName.trim() !== "" &&
      (adminPhoneNumber === "" || isPhoneValid)
    );
  };

  async function createAccount() {
    setCreatingAccount(true);

    AccountsService.createAccount({
      requestBody: {
        first_name: adminFirstName,
        last_name: adminLastName,
        email: user?.email as string,
        phone_number: adminPhoneNumber || null,
        reference_id: user?.sub,
        status: AccountStatus.ACTIVE,
      },
    })
      .then((accountResult) => {
        updateOrgRolesAndAccount({ account: accountResult });
        setStep(2);
      })
      .catch((e: ApiError) => {
        setError(e);
      })
      .finally(() => {
        setCreatingAccount(false);
      });
  }

  useEffect(() => {
    if (error) {
      Notifications.error(error.body.message);
    }
  }, [error]);

  return (
    <div className="flex flex-col">
      <h1 className="mt-5 headline-9">Welcome aboard!</h1>

      <div className="mt-2 mb-2 signup_text">
        Add some basic info to your profile. You can always change this later.
      </div>

      <label htmlFor="admin_first_name" className="mt-4 input-label">
        First Name
        <input
          id="admin_first_name"
          name="admin_first_name"
          type="text"
          className="input"
          placeholder="John"
          value={adminFirstName}
          onChange={(e) => setAdminFirstName(e.target.value)}
          disabled={creatingAccount}
          required
        />
      </label>

      <label htmlFor="admin_last_name" className="mt-4 input-label">
        Last Name
        <input
          id="admin_last_name"
          name="admin_last_name"
          type="text"
          className="input"
          placeholder="Doe"
          value={adminLastName}
          onChange={(e) => setAdminLastName(e.target.value)}
          disabled={creatingAccount}
          required
        />
      </label>

      <label htmlFor="admin_phone_number" className="mt-4 input-label">
        Phone Number (recommended)
        <div className="mt-2">
          <IntlTelInput
            inputProps={{
              className: "input mt-2",
              name: "admin_phone_number",
            }}
            onChangeValidity={setIsPhoneValid}
            onChangeNumber={(number) => setAdminPhoneNumber(number)}
            initOptions={{
              loadUtils: () => import("intl-tel-input/build/js/utils.js"),
              strictMode: true,
              autoPlaceholder: "aggressive",
              placeholderNumberType: "MOBILE",
              countryOrder: ["us", "gb", "ca"],
              initialCountry: "auto",
              geoIpLookup: (success, failure) => {
                fetch("https://ipapi.co/json/")
                  .then((res) => res.json())
                  .then((data) => success(data.country_code))
                  .catch(() => failure());
              },
              separateDialCode: true,
              nationalMode: true,
            }}
          />
        </div>
      </label>

      <div className="mt-4">
        <Button
          color={!isFormValid() ? ButtonColor.GRAY : ButtonColor.GREEN}
          disabled={creatingAccount || !isFormValid()}
          block={true}
          type="submit"
          onClick={createAccount}
        >
          Next <ArrowRightIcon />
        </Button>
      </div>
    </div>
  );
}

export default SignupOrgAdmin;
